import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  RefreshCw,
  Target,
  Briefcase,
  Layers,
  MousePointer2,
  ShieldCheck,
  Settings,
  LayoutDashboard,
  Workflow,
  Clock,
  Scale,
  BarChart3,
  Zap,
  Cpu,
  Award,
  Globe,
  ListChecks,
  SlidersHorizontal,
  GitCompareArrows,
  Filter,
  Rocket,
  Eye,
  EyeOff,
  ArrowUpRight,
  ChevronRight,
  Boxes,
  Coins,
  Save,
  Loader2,
} from 'lucide-react';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { adminMenuResponse, adminSettingsResponse } from '../Types/AdminType';
import {
  getAllArchitectureExperience,
  getAllBusinessImpactType,
  getAllCoreExpertise,
  getAllDecisionSpeed,
  getAllDomainExpertise,
  getAllHiringAuthority,
  getAllOwnerShipLevel,
  getAllProductScale,
  getAllReputationSignals,
  getAllRoleLevel,
  getAllStartupRiskPreference,
  getAllTargetRole,
  getAllTechnologyStack,
  getAllWorkFlexibility,
  getAllWorkStyle,
  getAllScoreConfig,
  getAllScoreValueMappingConfig,
  getAllSearchConfig,
  getAllAdminMenus,
  getAllAdminSettings,
  updateAdminMenu,
  updateAdminSetting,
} from '../Services/AdminService';

interface ListConfig {
  label: string;
  route?: string;
  icon: React.ReactNode;
  accent: string;
  fetch: () => Promise<any>;
}

const LISTS: ListConfig[] = [
  { label: 'Target Role', route: '/admin/target-role', icon: <Target size={20} />, accent: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400', fetch: getAllTargetRole },
  { label: 'Role Level', route: '/admin/role-level', icon: <Briefcase size={20} />, accent: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-400', fetch: getAllRoleLevel },
  { label: 'Product Scale', route: '/admin/product-scale', icon: <Layers size={20} />, accent: 'border-pink-500/30 bg-pink-500/10 text-pink-400', fetch: getAllProductScale },
  { label: 'Ownership Level', route: '/admin/owner-ship-level', icon: <MousePointer2 size={20} />, accent: 'border-rose-500/30 bg-rose-500/10 text-rose-400', fetch: getAllOwnerShipLevel },
  { label: 'Hiring Authority', route: '/admin/hiring-authority', icon: <ShieldCheck size={20} />, accent: 'border-purple-500/30 bg-purple-500/10 text-purple-400', fetch: getAllHiringAuthority },
  { label: 'Business Impact', route: '/admin/business-impact', icon: <Settings size={20} />, accent: 'border-slate-400/30 bg-slate-400/10 text-slate-300', fetch: getAllBusinessImpactType },
  { label: 'Architecture Experience', route: '/admin/architecture-experience', icon: <LayoutDashboard size={20} />, accent: 'border-blue-500/30 bg-blue-500/10 text-blue-400', fetch: getAllArchitectureExperience },
  { label: 'Work Style', route: '/admin/work-style', icon: <Workflow size={20} />, accent: 'border-violet-500/30 bg-violet-500/10 text-violet-400', fetch: getAllWorkStyle },
  { label: 'Work Flexibility', route: '/admin/work-flexibility', icon: <Clock size={20} />, accent: 'border-teal-500/30 bg-teal-500/10 text-teal-400', fetch: getAllWorkFlexibility },
  { label: 'Startup Risk Preference', route: '/admin/startup-risk-preference', icon: <Scale size={20} />, accent: 'border-orange-500/30 bg-orange-500/10 text-orange-400', fetch: getAllStartupRiskPreference },
  { label: 'Reputation Signals', route: '/admin/reputation-signals', icon: <BarChart3 size={20} />, accent: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400', fetch: getAllReputationSignals },
  { label: 'Decision Speed', route: '/admin/decision-speed', icon: <Zap size={20} />, accent: 'border-amber-500/30 bg-amber-500/10 text-amber-400', fetch: getAllDecisionSpeed },
  { label: 'Technology Stack', icon: <Cpu size={20} />, accent: 'border-sky-500/30 bg-sky-500/10 text-sky-400', fetch: getAllTechnologyStack },
  { label: 'Core Expertise', icon: <Award size={20} />, accent: 'border-lime-500/30 bg-lime-500/10 text-lime-400', fetch: getAllCoreExpertise },
  { label: 'Domain Expertise', icon: <Globe size={20} />, accent: 'border-teal-500/30 bg-teal-500/10 text-teal-300', fetch: getAllDomainExpertise },
];

const Toggle = ({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
      checked ? 'bg-cyan-500' : 'bg-slate-700'
    }`}
  >
    <span
      className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-5' : ''
      }`}
    />
  </button>
);

const RecruiterConfigComponent = () => {
  const navigate = useNavigate();
  const [optionCounts, setOptionCounts] = useState<Record<string, number>>({});
  const [scoreCount, setScoreCount] = useState(0);
  const [mappingCount, setMappingCount] = useState(0);
  const [searchCount, setSearchCount] = useState(0);
  const [menus, setMenus] = useState<adminMenuResponse[]>([]);
  const [scoreField, setScoreField] = useState<string>('');
  const [defaultInviteSetting, setDefaultInviteSetting] = useState<adminSettingsResponse | null>(null);
  const [defaultJobSetting, setDefaultJobSetting] = useState<adminSettingsResponse | null>(null);
  const [draftInvite, setDraftInvite] = useState('');
  const [draftJob, setDraftJob] = useState('');
  const [savingCredits, setSavingCredits] = useState(false);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string>('');
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const fetchCount = async (fetch: () => Promise<any>) => {
    try {
      const res = await fetch();
      return res?.success && Array.isArray(res.data) ? res.data.length : 0;
    } catch {
      return 0;
    }
  };

  const loadAll = async () => {
    setAlert(null);
    setLoading(true);
    try {
      const [counts, scoreRes, mappingRes, searchRes, menuRes, settingsRes] = await Promise.all([
        Promise.all(LISTS.map(async (list) => (await fetchCount(list.fetch)) as number)),
        getAllScoreConfig(),
        getAllScoreValueMappingConfig(),
        getAllSearchConfig(),
        getAllAdminMenus(),
        getAllAdminSettings(),
      ]);

      const nextCounts: Record<string, number> = {};
      LISTS.forEach((list, index) => {
        nextCounts[list.label] = counts[index] ?? 0;
      });
      setOptionCounts(nextCounts);

      if (scoreRes?.success && Array.isArray(scoreRes.data)) {
        setScoreCount(scoreRes.data.length);
        const enabled = scoreRes.data.find((s: any) => s.enabled) || scoreRes.data[0];
        setScoreField(enabled?.fieldName || '');
      }
      if (mappingRes?.success && Array.isArray(mappingRes.data)) setMappingCount(mappingRes.data.length);
      if (searchRes?.success && Array.isArray(searchRes.data)) setSearchCount(searchRes.data.length);
      if (menuRes?.success && Array.isArray(menuRes.data)) {
        setMenus(menuRes.data.sort((a: adminMenuResponse, b: adminMenuResponse) => a.adminMenuName.localeCompare(b.adminMenuName)));
      }
      if (settingsRes?.success && Array.isArray(settingsRes.data)) {
        const invite = settingsRes.data.find((s: any) => s.settingsKey === 'defaultInviteCredits') || null;
        const job = settingsRes.data.find((s: any) => s.settingsKey === 'defaultJobCredits') || null;
        setDefaultInviteSetting(invite);
        setDefaultJobSetting(job);
        setDraftInvite(invite ? String(invite.settingsValue) : '10');
        setDraftJob(job ? String(job.settingsValue) : '0');
      }
    } catch (err) {
      setAlert({
        variant: 'error',
        title: 'Load Error',
        message: 'An unexpected error occurred while loading the recruiter configuration.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const totalValues = Object.values(optionCounts).reduce((sum, count) => sum + count, 0);

  const handleMenuToggle = async (menu: adminMenuResponse, key: 'staus' | 'isDisplayOnUi', value: boolean) => {
    setAlert(null);
    setTogglingId(menu.id + key);
    try {
      const res = await updateAdminMenu(
        {
          adminMenuName: menu.adminMenuName,
          status: key === 'staus' ? value : menu.staus,
          isDisplayOnUi: key === 'isDisplayOnUi' ? value : menu.isDisplayOnUi,
        },
        menu.id
      );
      if (res.success) {
        setMenus((prev) => prev.map((m) => (m.id === menu.id ? { ...m, [key]: value } : m)));
        setAlert({
          variant: 'success',
          title: 'Success...!',
          message: `"${menu.adminMenuName}" has been updated successfully.`,
          autoCloseMs: 4000,
          onClose: () => setAlert(null),
        });
      } else {
        setAlert({ variant: 'error', title: 'Update Error', message: res.message, autoCloseMs: 5000, onClose: () => setAlert(null) });
      }
    } catch (err: any) {
      setAlert({
        variant: 'error',
        title: 'Update Error',
        message: err?.message || 'An unexpected error occurred.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setTogglingId('');
    }
  };

  const saveDefaultCredits = async () => {
    if (!defaultInviteSetting || !defaultJobSetting) return;
    setSavingCredits(true);
    setAlert(null);
    try {
      if (draftInvite !== '' && String(defaultInviteSetting.settingsValue) !== draftInvite) {
        await updateAdminSetting(
          {
            settingsKey: 'defaultInviteCredits',
            settingsValue: String(draftInvite),
            dataType: 'NUMBER',
            description: defaultInviteSetting.description,
            isActive: true,
          },
          defaultInviteSetting.id
        );
      }
      if (draftJob !== '' && String(defaultJobSetting.settingsValue) !== draftJob) {
        await updateAdminSetting(
          {
            settingsKey: 'defaultJobCredits',
            settingsValue: String(draftJob),
            dataType: 'NUMBER',
            description: defaultJobSetting.description,
            isActive: true,
          },
          defaultJobSetting.id
        );
      }
      const settingsRes = await getAllAdminSettings();
      const invite = settingsRes?.data?.find((s: any) => s.settingsKey === 'defaultInviteCredits') || null;
      const job = settingsRes?.data?.find((s: any) => s.settingsKey === 'defaultJobCredits') || null;
      setDefaultInviteSetting(invite);
      setDefaultJobSetting(job);
      setDraftInvite(invite ? String(invite.settingsValue) : '10');
      setDraftJob(job ? String(job.settingsValue) : '0');
      setAlert({
        variant: 'success',
        title: 'Success...!',
        message: 'Default credits updated. New accounts and companies will use these values.',
        autoCloseMs: 4000,
        onClose: () => setAlert(null),
      });
    } catch {
      setAlert({
        variant: 'error',
        title: 'Save Error',
        message: 'Failed to save default credits.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setSavingCredits(false);
    }
  };

  const stats = [
    { label: 'Categories', value: LISTS.length, icon: <Boxes size={18} />, accent: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400' },
    { label: 'Option Values', value: totalValues, icon: <ListChecks size={18} />, accent: 'border-blue-500/30 bg-blue-500/10 text-blue-400' },
    { label: 'Score Configs', value: scoreCount, icon: <SlidersHorizontal size={18} />, accent: 'border-violet-500/30 bg-violet-500/10 text-violet-400' },
    { label: 'Menus On UI', value: menus.filter((m) => m.isDisplayOnUi).length, icon: <Eye size={18} />, accent: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' },
  ];

  const engineCards = [
    {
      label: 'Score Config',
      subtitle: 'Weights, types & enable flags',
      value: scoreCount,
      route: '/admin/candidate-score-config',
      icon: <SlidersHorizontal size={22} />,
      accent: 'border-violet-500/30 bg-violet-500/10 text-violet-400',
    },
    {
      label: 'Value Mapping',
      subtitle: 'Field value → score lookup',
      value: mappingCount,
      route: scoreField ? `/admin/candidate-score-mapping-config/${encodeURIComponent(scoreField)}` : undefined,
      icon: <GitCompareArrows size={22} />,
      accent: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-400',
    },
    {
      label: 'Search Filters',
      subtitle: 'Recruiter-side search tiers',
      value: searchCount,
      route: '/admin/search-filter-config',
      icon: <Filter size={22} />,
      accent: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
    },
    {
      label: 'Jump Start',
      subtitle: 'Seed config data',
      value: null,
      route: '/admin/jumpstart-config',
      icon: <Rocket size={22} />,
      accent: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      <div className="mx-auto max-w-7xl space-y-8">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
              <Briefcase size={26} />
            </span>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-100 md:text-3xl">Recruiter Configurations</h1>
              <p className="mt-1 text-sm font-medium text-slate-400">
                Option lists, scoring engine and menu visibility used by the recruiter service.
              </p>
            </div>
          </div>
          <button
            onClick={loadAll}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800/80 px-4 py-2.5 text-sm font-bold text-slate-200 transition-colors hover:border-cyan-500/40 hover:text-cyan-300 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </header>

        <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.label} className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
              <div className="flex items-center justify-between">
                <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${stat.accent}`}>{stat.icon}</span>
                <span className="text-2xl font-black text-slate-100">{loading ? '—' : stat.value}</span>
              </div>
              <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{stat.label}</p>
            </div>
          ))}
        </section>

        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-black text-slate-100">Option Lists</h2>
              <p className="text-sm font-medium text-slate-400">Lookup values used while building a recruiter profile.</p>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="h-28 animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {LISTS.map((list, index) => {
                const clickable = Boolean(list.route);
                return (
                  <div
                    key={list.label}
                    onClick={() => list.route && navigate(list.route)}
                    className={`group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-5 transition-all ${
                      clickable ? 'cursor-pointer hover:-translate-y-0.5 hover:border-cyan-500/40 hover:shadow-lg hover:shadow-cyan-500/5' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-xl border ${list.accent}`}>
                        {list.icon}
                      </span>
                      <span className="text-xl font-black text-slate-100">{optionCounts[list.label] ?? 0}</span>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-bold text-slate-200">{list.label}</h3>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                          {optionCounts[list.label] ?? 0} values
                        </p>
                      </div>
                      {clickable ? (
                        <span className="flex items-center gap-0.5 text-xs font-bold text-cyan-400 opacity-0 transition-opacity group-hover:opacity-100">
                          Open <ChevronRight size={14} />
                        </span>
                      ) : (
                        <span className="rounded-lg border border-slate-700 bg-slate-800/60 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          API only
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <div className="mb-4">
            <h2 className="text-lg font-black text-slate-100">Matching Engine</h2>
            <p className="text-sm font-medium text-slate-400">Scoring, filtering and data seeding for recruiter matching.</p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {engineCards.map((card) => {
              const clickable = Boolean(card.route);
              return (
                <div
                  key={card.label}
                  onClick={() => card.route && navigate(card.route as string)}
                  className={`group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-5 transition-all ${
                    clickable ? 'cursor-pointer hover:-translate-y-0.5 hover:border-violet-500/40 hover:shadow-lg hover:shadow-violet-500/5' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <span className={`flex h-11 w-11 items-center justify-center rounded-xl border ${card.accent}`}>{card.icon}</span>
                    {card.value !== null && <span className="text-xl font-black text-slate-100">{loading ? '—' : card.value}</span>}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-200">{card.label}</h3>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{card.subtitle}</p>
                    </div>
                    {clickable && (
                      <span className="flex items-center gap-0.5 text-xs font-bold text-cyan-400 opacity-0 transition-opacity group-hover:opacity-100">
                        Open <ArrowUpRight size={14} />
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
        >
          <div className="mb-4">
            <h2 className="text-lg font-black text-slate-100">Default Credits</h2>
            <p className="text-sm font-medium text-slate-400">
              Credits granted automatically when a new account or company is created.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                  <Coins size={13} className="text-cyan-400" /> Invite Credits (new user)
                </label>
                <input
                  type="number"
                  min={0}
                  value={draftInvite}
                  onChange={(e) => setDraftInvite(e.target.value)}
                  disabled={loading}
                  className={`w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark] disabled:opacity-60`}
                />
                <p className="mt-1 text-[10px] font-semibold text-slate-500">
                  Balance set on every user at registration (invitations they can send).
                </p>
              </div>
              <div>
                <label className="mb-1.5 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                  <Briefcase size={13} className="text-violet-400" /> Job Credits (new company)
                </label>
                <input
                  type="number"
                  min={0}
                  value={draftJob}
                  onChange={(e) => setDraftJob(e.target.value)}
                  disabled={loading}
                  className={`w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-violet-500/50 [color-scheme:dark] disabled:opacity-60`}
                />
                <p className="mt-1 text-[10px] font-semibold text-slate-500">
                  Opening company balance for a recruiter's first domain company.
                </p>
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                onClick={saveDefaultCredits}
                disabled={savingCredits || loading}
                className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-60"
              >
                {savingCredits ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save Defaults
              </button>
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="mb-4">
            <h2 className="text-lg font-black text-slate-100">Menu Visibility</h2>
            <p className="text-sm font-medium text-slate-400">Toggle which admin configuration pages are shown on the UI.</p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                    <th className="px-5 py-3">Menu</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Visible on UI</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 4 }).map((_, index) => (
                        <tr key={index} className="border-b border-slate-800/60 last:border-0">
                          <td colSpan={3} className="px-5 py-4">
                            <div className="h-5 animate-pulse rounded bg-slate-800/60" />
                          </td>
                        </tr>
                      ))
                    : menus.map((menu) => (
                        <tr key={menu.id} className="border-b border-slate-800/60 last:border-0 transition-colors hover:bg-slate-800/20">
                          <td className="px-5 py-3">
                            <div className="font-bold text-slate-200">{menu.adminMenuName}</div>
                            <div className="text-xs font-medium text-slate-500">
                              {menu.isDisplayOnUi ? (
                                <span className="inline-flex items-center gap-1 text-emerald-400">
                                  <Eye size={12} /> Public
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-slate-500">
                                  <EyeOff size={12} /> Hidden
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            <Toggle
                              checked={Boolean(menu.staus)}
                              disabled={togglingId === menu.id + 'staus'}
                              onChange={(value) => handleMenuToggle(menu, 'staus', value)}
                            />
                          </td>
                          <td className="px-5 py-3">
                            <Toggle
                              checked={Boolean(menu.isDisplayOnUi)}
                              disabled={togglingId === menu.id + 'isDisplayOnUi'}
                              onChange={(value) => handleMenuToggle(menu, 'isDisplayOnUi', value)}
                            />
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default RecruiterConfigComponent;